import React, { useEffect, useState } from 'react';
import { NavLink, useNavigate } from 'react-router-dom';
import styled, { css } from 'styled-components';
import { useTranslation } from 'react-i18next';
import AuthModal from './AuthModal';
import api from '../api/axiosConfig';

const breakpoints = {
  sm: '480px',
  md: '900px',
  lg: '1025px',
};

const DesktopBar = styled.div`
  width: 100%;
  display: grid;
  grid-template-columns: 1fr auto 1fr;
  align-items: center;
  column-gap: 16px;
  box-sizing: border-box;

  @media (max-width: ${breakpoints.lg}) {
    display: none;
  }
`;

const DesktopLeft = styled.div`
  display: flex;
  align-items: center;
`;

const CenterLinks = styled.div`
  display: flex;
  justify-content: center;
  gap: clamp(20px, 5vw, 80px);
  flex-wrap: nowrap;
`;

const NavLinkStyled = styled(NavLink)`
  text-decoration: none;
  color: #053969;
  font-size: clamp(22px, 2.2vw, 35px);
  font-weight: 500;
  letter-spacing: 0.3px;
  transition: 0.25s ease;
  padding: 6px 10px;
  border: 1px solid ${(props) => props.$borderColor || '#ec9907'};
  border-left: none;
  border-top: none;
  border-radius: 30px;
  font-family: 'Pristina', Regular, cursive;

  &:hover {
    color: #000000ff;
    transform: translateY(-2px);
  }

  &.active {
    color: #e63946;
    font-weight: 600;
  }

  @media (max-width: ${breakpoints.lg}) {
    font-size: 22px;
    width: 100%;
    text-align: left;
    justify-content: flex-start;
    display: block;
  }
`;

const RightBlock = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 15px;
`;

const AuthButtons = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
`;

const AuthButton = styled.button`
  border-radius: 999px;
  padding: 6px 14px;
  font-size: 0.95rem;
  cursor: pointer;
  border: 1px solid #053969;
  background: ${(props) => (props.$primary ? '#053969' : 'transparent')};
  color: ${(props) => (props.$primary ? '#ffffff' : '#053969')};
  transition: 0.2s ease;

  &:hover {
    transform: translateY(-1px);
    opacity: 0.9;
  }
`;

const LangSwitcher = styled.div`
  display: flex;
  gap: 8px;
`;

const LangButton = styled.button`
  background: transparent;
  border: none;
  cursor: pointer;
  font-size: 1rem;

  &.active {
    font-weight: 700;
    text-decoration: underline;
  }
`;

/* ================= MOBILE NAVBAR / BURGER ================= */

const MobileBar = styled.div`
  display: flex;
  justify-content: flex-end;
  align-items: center;

  @media (min-width: ${breakpoints.lg}) {
    display: none;
  }
`;

const BurgerButton = styled.button`
  border: none;
  background: none;
  border-radius: 10px;
  padding: 8px 10px;
  cursor: pointer;

  display: flex;
  flex-direction: column;
  justify-content: center;
  gap: 6px;

  span {
    display: block;
    width: 30px;
    height: 3px;
    background: #053969;
    border-radius: 999px;
    transition: all 0.4s ease;
  }

  &:hover span {
    opacity: 0.8;
  }

  ${(props) =>
    props.$open &&
    css`
      span:nth-of-type(1) {
        transform: translateY(9px) rotate(45deg);
      }
      span:nth-of-type(2) {
        opacity: 0;
      }
      span:nth-of-type(3) {
        transform: translateY(-9px) rotate(-45deg);
      }
    `}
`;

const MobileOverlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.45);
  z-index: 2000;
  opacity: ${(p) => (p.$open ? 1 : 0)};
  pointer-events: ${(p) => (p.$open ? 'auto' : 'none')};
  transition: 0.25s ease;

  @media (min-width: ${breakpoints.lg}) {
    display: none;
  }
`;

const Sidebar = styled.div`
  position: fixed;
  top: 0;
  right: 0;
  height: 100vh;
  border-radius: 50px 0 0 50px;
  width: 300px;
  max-width: 80%;
  background: #ffffffff;
  box-shadow: -6px 0 25px rgba(0, 0, 0, 0.25);
  z-index: 2001;

  padding: 20px 18px;
  box-sizing: border-box;

  display: flex;
  flex-direction: column;

  transform: translateX(100%);
  transition: transform 0.35s cubic-bezier(0.4, 0, 0.2, 1);

  ${(p) =>
    p.$open &&
    css`
      transform: translateX(0);
    `}

  @media (min-width: ${breakpoints.lg}) {
    display: none;
  }
`;

const SidebarHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;

  font-size: 1.3rem;
  font-weight: 600;
  color: #053969;
  padding-bottom: 10px;
  border-bottom: 1px solid rgba(0, 0, 0, 0.08);
`;

const HeaderLeft = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;

const HeaderLang = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;

  font-size: 1rem;
  font-weight: 500;
`;

const CloseBtn = styled.button`
  border: none;
  background: #ffffffff;
  border-radius: 50px;
  border: 1px solid #053969;
  padding: 3px 8px;
  cursor: pointer;

  display: flex;
  align-items: center;
  justify-content: center;

  font-size: 1.1rem;
  color: #053969;

  &:hover {
    background: #dcdcdc;
  }
`;

const SidebarLinks = styled.div`
  display: flex;
  flex-direction: column;
  margin-top: 18px;
  gap: 12px;
`;

const SidebarLink = styled(NavLink)`
  font-size: 1.15rem;
  text-decoration: none;
  color: #053969;

  padding: 10px 12px;
  border-radius: 999px;
  border: 1px solid ${(p) => p.$borderColor || '#ec9907'};
  border-left: none;
  border-top: none;

  &:hover {
    background: #f7f7f7;
    color: #e63946;
  }

  &.active {
    color: #e63946;
    font-weight: 600;
  }
`;

const SidebarFooter = styled.div`
  margin-top: 14px;
  display: flex;
  align-items: center;
  gap: 12px;
`;

const LogoImg = styled.img`
  width: 52px;
  height: 52px;
  object-fit: contain;
  border-radius: 12px;
`;

/* ================= FIX "/api/api/..." ================= */

function safeApiPath(path) {
  const p = String(path || '');
  if (!p) return p;

  const normalized = p.replace(/\/{2,}/g, '/');

  if (normalized.startsWith('/api/')) return normalized.replace(/^\/api/, '');
  if (normalized.startsWith('api/')) return normalized.replace(/^api/, '');

  if (normalized === '/api') return '/';
  if (normalized === 'api') return '/';

  return normalized;
}

/* ================= COMPONENT ================= */

function Navbar({ homeData, onMobileMenuChange }) {
  const { i18n, t } = useTranslation();
  const navigate = useNavigate();

  const [open, setOpen] = useState(false);
  const currentLang = i18n.language || 'en';
  const borderColor = homeData?.border_color;

  const toggleMobile = () => {
    setOpen((prev) => {
      const next = !prev;
      onMobileMenuChange?.(next);
      return next;
    });
  };

  const closeMobile = () => {
    setOpen(false);
    onMobileMenuChange?.(false);
  };

  const [authOpen, setAuthOpen] = useState(false);
  const [authMode, setAuthMode] = useState('signin');

  const openSignIn = () => {
    setAuthMode('signin');
    setAuthOpen(true);
  };

  const openSignUp = () => {
    setAuthMode('signup');
    setAuthOpen(true);
  };

  // ✅ AUTH STATE
  const [me, setMe] = useState(null);

  const loadMe = async () => {
    const token = localStorage.getItem('access');
    if (!token) {
      setMe(null);
      return;
    }
    try {
      const res = await api.get(safeApiPath('/me/'));
      setMe(res.data);
    } catch (e) {
      console.error('Unable to load current user:', e);
    }
  };

  useEffect(() => {
    loadMe();
  }, []);

  const logout = () => {
    localStorage.removeItem('access');
    localStorage.removeItem('refresh');
    setMe(null);
    navigate('/');
  };

  const changeLang = (lng) => {
    i18n.changeLanguage(lng);
    localStorage.setItem('lang', lng);
  };

  return (
    <>
      {/* ===== DESKTOP ===== */}
      <DesktopBar>
        <DesktopLeft>
          {homeData?.logo ? <LogoImg src={homeData.logo} alt="Logo" /> : null}
        </DesktopLeft>

        <CenterLinks>
          <NavLinkStyled to="/catalog" $borderColor={borderColor}>
            {t('nav.catalog')}
          </NavLinkStyled>
          <NavLinkStyled to="/blog" $borderColor={borderColor}>
            {t('nav.blog')}
          </NavLinkStyled>
          <NavLinkStyled to="/about" $borderColor={borderColor}>
            {t('nav.about')}
          </NavLinkStyled>
          <NavLinkStyled to="/contacts" $borderColor={borderColor}>
            {t('nav.contacts')}
          </NavLinkStyled>
        </CenterLinks>

        <RightBlock>
          <AuthButtons>
            {me ? (
              <>
                <AuthButton onClick={() => navigate('/me')}>{t('nav.myAccount')}</AuthButton>
                <AuthButton $primary onClick={logout}>
                  {t('nav.logout')}
                </AuthButton>
              </>
            ) : (
              <>
                <AuthButton onClick={openSignIn}>{t('nav.signIn')}</AuthButton>
                <AuthButton $primary onClick={openSignUp}>
                  {t('nav.signUp')}
                </AuthButton>
              </>
            )}
          </AuthButtons>

          <LangSwitcher>
            <LangButton
              onClick={() => changeLang('it')}
              className={currentLang.startsWith('it') ? 'active' : ''}
            >
              IT
            </LangButton>
            <LangButton
              onClick={() => changeLang('en')}
              className={currentLang.startsWith('en') ? 'active' : ''}
            >
              EN
            </LangButton>
          </LangSwitcher>
        </RightBlock>
      </DesktopBar>

      {/* ===== MOBILE / TABLET ===== */}
      <MobileBar>
        <BurgerButton
          type="button"
          onClick={toggleMobile}
          aria-expanded={open ? 'true' : 'false'}
          aria-label={t('aria.toggleNavigation')}
          $open={open}
        >
          <span />
          <span />
          <span />
        </BurgerButton>
      </MobileBar>

      <MobileOverlay $open={open} onClick={closeMobile} />

      <Sidebar
        $open={open}
        onClick={(e) => {
          e.stopPropagation();
        }}
      >
        <SidebarHeader>
          <HeaderLeft>
            <span>{t('nav.menu')}</span>
            <HeaderLang>
              <LangButton
                onClick={() => {
                  changeLang('it');
                  closeMobile();
                }}
                className={currentLang.startsWith('it') ? 'active' : ''}
              >
                IT
              </LangButton>
              {' / '}
              <LangButton
                onClick={() => {
                  changeLang('en');
                  closeMobile();
                }}
                className={currentLang.startsWith('en') ? 'active' : ''}
              >
                EN
              </LangButton>
            </HeaderLang>
          </HeaderLeft>

          <CloseBtn onClick={closeMobile}>✕</CloseBtn>
        </SidebarHeader>

        <SidebarFooter>
          {me ? (
            <>
              <AuthButton
                onClick={() => {
                  closeMobile();
                  navigate('/me');
                }}
              >
                {t('nav.myAccount')}
              </AuthButton>

              <AuthButton
                $primary
                onClick={() => {
                  closeMobile();
                  logout();
                }}
              >
                {t('nav.logout')}
              </AuthButton>
            </>
          ) : (
            <>
              <AuthButton
                onClick={() => {
                  closeMobile();
                  openSignIn();
                }}
              >
                {t('nav.signIn')}
              </AuthButton>

              <AuthButton
                $primary
                onClick={() => {
                  closeMobile();
                  openSignUp();
                }}
              >
                {t('nav.signUp')}
              </AuthButton>
            </>
          )}
        </SidebarFooter>

        <AuthModal
          open={authOpen}
          mode={authMode}
          onClose={() => setAuthOpen(false)}
          onAuthSuccess={(user) => {
            setMe(user);
          }}
        />

        <SidebarLinks>
          <SidebarLink to="/catalog" onClick={closeMobile} $borderColor={borderColor}>
            {t('nav.catalog')}
          </SidebarLink>
          <SidebarLink to="/blog" onClick={closeMobile} $borderColor={borderColor}>
            {t('nav.blog')}
          </SidebarLink>
          <SidebarLink to="/about" onClick={closeMobile} $borderColor={borderColor}>
            {t('nav.about')}
          </SidebarLink>
          <SidebarLink to="/contacts" onClick={closeMobile} $borderColor={borderColor}>
            {t('nav.contacts')}
          </SidebarLink>
        </SidebarLinks>
      </Sidebar>
    </>
  );
}

export default Navbar;
